/**
 * Pure bootstrap resolution for docs/345's single-/multi-provider admin. The
 * top-level provider list is the build-owned trust boundary. Hostname and
 * fetched layers may only select or remove entries from it; neither can add a
 * backend origin that the static build did not already own.
 */

export type ProviderDeploymentMode = "single" | "multi";

export interface AdminProviderRef {
  /** Stable config-owned id used to namespace this provider's browser state. */
  id: string;
  label: string;
  /** Backend origin. Paths, credentials, query strings, and fragments are discarded. */
  backendUrl: string;
}

export interface FetchedProviderDeploymentConfig {
  /** Optional fetched provider descriptions. Every entry must retain a build-owned id and origin. */
  providers?: readonly AdminProviderRef[];
  /** Optional subtract-only allow-list of build-owned provider ids. */
  providerIds?: readonly string[];
  /** Optional active-provider selection. It can only select from the providers still in scope. */
  activeProviderId?: string;
}

export interface ProviderDeploymentConfig {
  mode: ProviderDeploymentMode;
  /** Lowest-precedence, build-owned origin allow-list. */
  providers: readonly AdminProviderRef[];
  /** Exact hostname to build-owned provider id. A match locks the resolution to that provider. */
  hostnameMap?: Readonly<Record<string, string>>;
  defaultProviderId?: string;
  /** Highest-precedence runtime layer. It is always intersected with the build-owned set. */
  fetchedConfig?: FetchedProviderDeploymentConfig;
}

export interface ProviderDeployment {
  mode: ProviderDeploymentMode;
  /** Providers remaining after hostname/fetched narrowing, in build order. */
  providers: readonly AdminProviderRef[];
  /** The normalized, build-owned backend origins represented by `providers`. */
  backendOrigins: readonly string[];
  activeProvider: AdminProviderRef | null;
  activeProviderId: string | null;
  showSwitcher: boolean;
}

function normalizeOrigin(value: string): string | null {
  try {
    const url = new URL(value);
    if ((url.protocol !== "https:" && url.protocol !== "http:") || !url.hostname) return null;
    return url.origin;
  } catch {
    return null;
  }
}

function normalizeProvider(provider: AdminProviderRef): AdminProviderRef | null {
  const id = provider.id.trim();
  const origin = normalizeOrigin(provider.backendUrl.trim());
  if (!id || !origin) return null;
  return { id, label: provider.label, backendUrl: origin };
}

function buildProviders(config: ProviderDeploymentConfig): AdminProviderRef[] {
  const ids = new Set<string>();
  const providers: AdminProviderRef[] = [];
  for (const candidate of config.providers) {
    const provider = normalizeProvider(candidate);
    if (!provider || ids.has(provider.id)) continue;
    ids.add(provider.id);
    providers.push(provider);
  }

  if (config.mode === "multi" || providers.length <= 1) return providers;
  const preferred = providers.find((provider) => provider.id === config.defaultProviderId);
  return [preferred ?? providers[0]];
}

/**
 * Resolves build env <- hostname map <- fetched config (rightmost wins by
 * narrowing). Invalid or untrusted later-layer entries fail closed and never
 * expand the build-owned provider/origin set.
 */
export function resolveProviderDeployment(config: ProviderDeploymentConfig, hostname: string): ProviderDeployment {
  const owned = buildProviders(config);
  const ownedById = new Map(owned.map((provider) => [provider.id, provider]));
  let providers = owned;

  const hostnameProviderId = config.hostnameMap?.[hostname.trim().toLowerCase()];
  const hostnameProvider = hostnameProviderId ? ownedById.get(hostnameProviderId) : undefined;
  const hostnameLocked = !!hostnameProvider;
  if (hostnameProvider) providers = [hostnameProvider];

  const fetched = config.fetchedConfig;
  if (fetched?.providers) {
    const fetchedById = new Map<string, AdminProviderRef>();
    for (const candidate of fetched.providers) {
      const provider = normalizeProvider(candidate);
      const buildProvider = provider && ownedById.get(provider.id);
      if (provider && buildProvider && provider.backendUrl === buildProvider.backendUrl) {
        fetchedById.set(provider.id, provider);
      }
    }
    providers = providers.flatMap((provider) => {
      const fetchedProvider = fetchedById.get(provider.id);
      return fetchedProvider ? [fetchedProvider] : [];
    });
  }

  if (fetched?.providerIds) {
    const allowedIds = new Set(fetched.providerIds);
    providers = providers.filter((provider) => allowedIds.has(provider.id));
  }

  if (!hostnameLocked && fetched?.activeProviderId) {
    const selected = providers.find((provider) => provider.id === fetched.activeProviderId);
    providers = selected ? [selected] : [];
  }

  const defaultProvider = providers.find((provider) => provider.id === config.defaultProviderId);
  const activeProvider = providers[0] ? (defaultProvider ?? providers[0]) : null;

  return {
    mode: config.mode,
    providers,
    backendOrigins: providers.map((provider) => provider.backendUrl),
    activeProvider,
    activeProviderId: activeProvider?.id ?? null,
    showSwitcher: config.mode === "multi" && providers.length > 1,
  };
}
